/**
 * Driver — Tier badge (G1; G3.E)
 *
 * Driver-local component used by DriverDashboard to render the
 * customer-widget tier (eco / business / van) on each ride card so the
 * driver knows what vehicle category was booked.
 *
 * Deliberately NOT imported from the AdminV3 TierBadge to keep the
 * AdminV3 module boundary clean and avoid coupling the driver bundle
 * to AdminV3 internals. The 3-key colour map below MIRRORS the AdminV3
 * TierBadge styling exactly so the visual signal is consistent across
 * operator and driver surfaces.
 *
 * Rendering rules:
 *   - eco       -> emerald pill, label "ECO"
 *   - business  -> slate    pill, label "BUSINESS"
 *   - van       -> amber    pill, label "VAN"
 *   - null / undefined / unknown string / "standard" / "priority"
 *               -> muted em-dash "—" (no claim is made for legacy or
 *                  removed-tier bookings; G3.E removed PRIORITY from
 *                  the customer-widget tier vocabulary)
 *
 * Pure presentation. No fetches. No state. No side effects.
 */

import React from 'react';

const TIER_META = {
  eco:      { pill: 'bg-emerald-50 text-emerald-700 border border-emerald-200', label: 'ECO' },
  business: { pill: 'bg-slate-100  text-slate-700   border border-slate-200',   label: 'BUSINESS' },
  van:      { pill: 'bg-amber-50   text-amber-700   border border-amber-200',   label: 'VAN' },
};

const DriverTierBadge = ({ tier }) => {
  const key = typeof tier === 'string' ? tier.toLowerCase() : null;
  const meta = key && TIER_META[key];
  if (!meta) {
    return <span className="text-neutral-500" data-testid="driver-tier-badge-empty">—</span>;
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${meta.pill}`}
      data-testid={`driver-tier-badge-${key}`}
    >
      {meta.label}
    </span>
  );
};

export default DriverTierBadge;
